import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

// Category colors (soft pastels, no emoji)
const CATEGORY_COLORS = {
  hospitality: { bg: '#FEF3C7', text: '#92400E', label: 'Hospitality' },
  security: { bg: '#DBEAFE', text: '#1E40AF', label: 'Security' },
  warehouse: { bg: '#FFEDD5', text: '#9A3412', label: 'Warehouse' },
  retail: { bg: '#FCE7F3', text: '#9D174D', label: 'Retail' },
  events: { bg: '#EDE9FE', text: '#5B21B6', label: 'Events' },
  cleaning: { bg: '#CCFBF1', text: '#115E59', label: 'Cleaning' },
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    axios.get('/api/shifts').then(r => {
      const open = (Array.isArray(r.data) ? r.data : r.data?.data || []).filter(s => s.status === 'open');
      setShifts(open.slice(0, 6));
    }).catch(err => {
      console.log('Shift fetch failed:', err);
      setShifts([]);
    });

    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const getCategoryColor = (title) => {
    if (title.toLowerCase().includes('barista') || title.toLowerCase().includes('hospitality')) return CATEGORY_COLORS.hospitality;
    if (title.toLowerCase().includes('security')) return CATEGORY_COLORS.security;
    if (title.toLowerCase().includes('warehouse')) return CATEGORY_COLORS.warehouse;
    if (title.toLowerCase().includes('retail')) return CATEGORY_COLORS.retail;
    if (title.toLowerCase().includes('event')) return CATEGORY_COLORS.events;
    if (title.toLowerCase().includes('clean')) return CATEGORY_COLORS.cleaning;
    return CATEGORY_COLORS.hospitality;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHelmet
        title="ShiftMatch — Find Casual Work & Get Paid Today"
        description="Australia's fastest way to find casual shifts. Get paid same day. Fair Work compliant."
      />

      {/* ── NAVBAR ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? 'bg-white border-b border-gray-200 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className={`font-bold text-lg ${scrolled ? 'text-gray-900' : 'text-white'}`}>ShiftMatch</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className={`text-sm font-semibold px-4 py-2 rounded-lg transition ${scrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/80 hover:text-white'}`}>
              Log in
            </Link>
            <Link to="/register" className="text-sm font-bold px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition">
              Get started free
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative bg-gradient-to-br from-emerald-600 to-emerald-700 pt-32 pb-16 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4">
              Find work today.<br />Get paid same day.
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Browse shifts, apply in seconds, work, and get paid to your account the same day. No CV. No waiting. Fair Work guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register" className="inline-block px-7 py-3 bg-white text-emerald-700 font-bold rounded-lg hover:bg-gray-50 transition text-center">
                Get started free
              </Link>
              <Link to="/register" className="inline-block px-7 py-3 bg-white/20 border border-white/40 text-white font-bold rounded-lg hover:bg-white/30 transition text-center">
                View all shifts
              </Link>
            </div>
          </div>

          {/* Trust badges */}
          <div className="grid sm:grid-cols-4 gap-4 mt-16">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
              <div className="text-white font-bold text-lg mb-1">Fair Work</div>
              <div className="text-white/80 text-sm">All shifts at award rates</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
              <div className="text-white font-bold text-lg mb-1">Paid Today</div>
              <div className="text-white/80 text-sm">Same-day or 48-hour payout</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
              <div className="text-white font-bold text-lg mb-1">Verified</div>
              <div className="text-white/80 text-sm">All employers ABN-checked</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
              <div className="text-white font-bold text-lg mb-1">Protected</div>
              <div className="text-white/80 text-sm">Escrow-secured payments</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LIVE SHIFTS SECTION ── */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2">
            Live Shifts Now Hiring
          </h2>
          <p className="text-gray-600">
            {shifts.length} positions available across Australia
          </p>
        </div>

        {shifts.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shifts.map(shift => {
              const category = getCategoryColor(shift.title);
              const startDate = new Date(shift.date);
              const dateStr = startDate.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' });

              return (
                <div key={shift.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition border border-gray-200 overflow-hidden">
                  {/* Urgency/Status bar */}
                  <div className="h-1 bg-rose-400" />

                  <div className="p-5">
                    {/* Header with category tag and price */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div
                        style={{ backgroundColor: category.bg, color: category.text }}
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                      >
                        {category.label}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-black text-gray-900">
                          ${shift.hourly_rate}
                        </div>
                        <div className="text-xs text-gray-500">/hour</div>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                      {shift.title}
                    </h3>

                    {/* Location & Duration */}
                    <div className="text-sm text-gray-600 mb-4">
                      <div>{shift.location}</div>
                      <div className="text-gray-500">
                        {shift.start_time} - {shift.end_time}
                      </div>
                    </div>

                    {/* Trust signals */}
                    <div className="text-xs text-gray-500 space-y-1 mb-4 pb-4 border-b border-gray-100">
                      <div>Fair Work award rate</div>
                      <div>Paid within 48 hours</div>
                      <div>Escrow protected</div>
                    </div>

                    {/* Date & CTA */}
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">{dateStr}</div>
                      <button
                        onClick={() => navigate('/register')}
                        className="inline-block px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-lg transition"
                      >
                        Apply now
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <div className="text-gray-600 mb-3">Loading shifts...</div>
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/register" className="inline-block text-emerald-600 font-bold hover:text-emerald-700">
            View all shifts on ShiftMatch →
          </Link>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-black text-gray-900 text-center mb-12">
            How it works
          </h2>

          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-black text-emerald-700">1</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Browse shifts</h3>
              <p className="text-gray-600">Filter by location, pay rate, and schedule. See exactly what you'll earn before you apply.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-black text-emerald-700">2</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Apply instantly</h3>
              <p className="text-gray-600">No CV. No interview. One tap. Employers respond within hours, often minutes.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-black text-emerald-700">3</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Get paid today</h3>
              <p className="text-gray-600">Work, clock out, and your pay arrives in your account same day.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-black text-gray-900 text-center mb-12">
            Workers love ShiftMatch
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Sarah M.', role: 'Hospitality', earned: '$1,240', text: 'Found weekend work in 2 hours. The urgent shift feature is a lifesaver when I need quick cash.' },
              { name: 'Marcus J.', role: 'Construction', earned: '$2,890', text: 'Better than any agency. Employers respond, pay is fair, and I get paid same day.' },
              { name: 'Priya K.', role: 'Events', earned: '$680', text: 'I love seeing exactly what I\'ll earn before I apply. No surprises, no games.' },
            ].map(review => (
              <div key={review.name} className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <span key={i} className="text-amber-400">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{review.text}"</p>
                <div className="border-t pt-4">
                  <div className="font-bold text-gray-900">{review.name}</div>
                  <div className="text-sm text-gray-600">{review.role} • Earned {review.earned}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER CTA ── */}
      <section className="bg-emerald-600 py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black text-white mb-4">
            Ready to find your next shift?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Join thousands of Australian workers earning fair pay on their own terms.
          </p>
          <Link to="/register" className="inline-block px-8 py-3 bg-white text-emerald-700 font-bold rounded-lg hover:bg-gray-50 transition">
            Get started free
          </Link>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center text-sm">
          <p>ShiftMatch Pty Ltd • ABN 12 345 678 901</p>
          <p className="mt-2">Making casual work fair in Australia</p>
        </div>
      </footer>
    </div>
  );
}
